import { emptyStringToNull } from '@openx/components-form/emptyStringToNullable';
import {
  FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL,
  FLOAT_NUMBER_WITH_MAX_TWO_DECIMALS,
} from '@openx/components-form/validationRegexes';
import { ACL_TYPE } from '@openx/types/aclType';
import { ThirdPartyFeeConfigRevenueMethod } from '@openx/types/gqlTypes';
import { microToPercentWithPrecision } from '@openx/utils/lib/microToPercent';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { array, number, object, string } from 'yup';
import { useDealFormContext } from '../DealForm.context';

export const useThirdPartyFeesConfigValidationSchema = () => {
  const { thirdPartyPartnerOptions } = useDealFormContext();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const { t } = useTranslation();

  const partner = partnerId ? thirdPartyPartnerOptions[partnerId] : null;

  const thirdPartyFeeConfigSchema = useMemo(() => {
    const maxCpmCapParsed = Number.parseFloat(partner?.max_cpm_cap ?? '');

    const maxShareParsed = Number.parseFloat(microToPercentWithPrecision(partner?.max_share) ?? '');
    const maxShareApi = Number.isFinite(maxShareParsed) ? maxShareParsed : 99.9;
    const maxShare = Math.min(maxShareApi, 94);

    const grossCpmCapLabel = t('Fixed CPM');
    const grossShareLabel = t('Percent of Media');
    const platformShareLabel = t('Platform Fee');

    return object({
      gross_cpm_cap: number()
        .transform(emptyStringToNull)
        .nullable()
        .when('revenue_method', {
          is: ThirdPartyFeeConfigRevenueMethod.Cpm,
          then: schema =>
            schema
              .typeError(t('{field} must be a number', { field: grossCpmCapLabel }))
              .min(0, t('{field} must be greater than or equal to {min}', { field: grossCpmCapLabel, min: '0' }))
              .test({
                exclusive: true,
                message: t(`{field} must be less than or equal to {max}`, {
                  field: grossCpmCapLabel,
                  max: maxCpmCapParsed.toFixed(2),
                }),
                name: 'maxCmpCap',
                test: value => !Number.isFinite(maxCpmCapParsed) || value! <= maxCpmCapParsed,
              })
              .test({
                exclusive: true,
                message: t('{field} may contain only 2 digits after decimal point', { field: grossCpmCapLabel }),
                name: 'maxTwoDigitsAfterDecimal',
                test: value => FLOAT_NUMBER_WITH_MAX_TWO_DECIMALS.test(value?.toString() ?? '0'),
              })
              .required(t('{field} is required', { field: grossCpmCapLabel })),
        }),
      gross_share: number()
        .transform(emptyStringToNull)
        .nullable()
        .when('revenue_method', {
          is: ThirdPartyFeeConfigRevenueMethod.PoM,
          then: schema =>
            schema
              .typeError(t('{field} must be a number', { field: grossShareLabel }))
              .min(0, t('{field} must be greater than or equal to {min}', { field: grossShareLabel, min: '0%' }))
              .max(
                maxShare,
                t(`{field} must be less than or equal to {max}`, { field: grossShareLabel, max: `${maxShare}%` })
              )
              .test({
                exclusive: true,
                message: t('{field} may contain only 1 digit after decimal point', { field: grossShareLabel }),
                name: 'maxOneDigitAfterDecimal',
                test: value => FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL.test(value?.toString() ?? '0'),
              })
              .required(t('{field} is required', { field: grossShareLabel })),
        }),
      partner_id: string()
        .nullable()
        .test({
          exclusive: true,
          message: t('Max fees must be fetched'),
          name: 'maxFeesFetched',
          test: partnerId => {
            // this is a workaround to get the reference to the current `partner_id`, which is needed to load max fees
            if (partnerId) {
              setPartnerId(partnerId);
            }

            return true;
          },
        })
        .required(t('{field} is required', { field: t('Partner') })),
      platform_share: number()
        .transform(emptyStringToNull)
        .nullable()
        .typeError(t('{field} must be a number', { field: platformShareLabel }))
        .when({
          is: () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_WRITE)),
          then: schema =>
            schema
              .min(0, t('{field} must be greater than or equal to {min}', { field: platformShareLabel, min: '0%' }))
              .max(99.9, t(`{field} must be less than or equal to {max}`, { field: platformShareLabel, max: `99.9%` }))
              .test({
                exclusive: true,
                message: t('{field} may contain only 1 digit after decimal point', { field: platformShareLabel }),
                name: 'maxOneDigitAfterDecimal',
                test: value => FLOAT_NUMBER_WITH_MAX_ONE_DECIMAL.test(value?.toString() ?? '0'),
              }),
        }),
    });
  }, [isAllowed, partner?.max_cpm_cap, partner?.max_share, t]);

  const thirdPartyFeesConfigSchema = useMemo(
    () =>
      array()
        .length(1)
        .when('third_party_fees_config_enabled', {
          is: value => value && isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_WRITE)),
          then: schema => schema.of(thirdPartyFeeConfigSchema),
        }),
    [isAllowed, thirdPartyFeeConfigSchema]
  );

  return {
    thirdPartyFeesConfigSchema,
  };
};
